import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/** One celebratory burst — shared by /connections (connected) and /onboarding (first message out). */
const CSS = `
.btcg-confetti{position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:2000}
.btcg-confetti i{position:absolute;width:9px;height:14px;border-radius:2px;opacity:0;animation:btcg-burst var(--d) linear var(--delay) forwards}
@keyframes btcg-burst{
0%{opacity:1;transform:translate(0,0) rotate(0);animation-timing-function:cubic-bezier(.2,.7,.4,1)}
35%{opacity:1;transform:translate(calc(var(--x) * .55),calc(var(--up) * -1)) rotate(calc(var(--r) * .4));animation-timing-function:cubic-bezier(.5,0,.8,.6)}
85%{opacity:1}
100%{opacity:0;transform:translate(var(--x),var(--fall)) rotate(var(--r))}}
@media (prefers-reduced-motion:reduce){.btcg-confetti{display:none}}
`;

const CONFETTI_COLORS = ['#1677cc', '#53bdeb', '#25d366', '#fbbf24', '#a78bfa', '#f472b6'];

/** A one-shot burst from `origin` (the success badge): pieces shoot up and out,
 *  then fall with gravity. Portaled to <body> — an ancestor with a transform (the
 *  card's entrance animation) would otherwise trap position:fixed inside the card. */
export const ConfettiBurst: React.FC<{ origin: { x: number; y: number } }> = ({ origin }) => {
  const [alive, setAlive] = useState(true);
  useEffect(() => {
    const id = window.setTimeout(() => setAlive(false), 3000);
    return () => window.clearTimeout(id);
  }, []);
  if (!alive || typeof document === 'undefined') return null;
  const isRtl = getComputedStyle(document.body).direction === 'rtl' || document.documentElement.dir === 'rtl';
  return createPortal(
    <div className="btcg-confetti" aria-hidden>
      <style>{CSS}</style>
      {Array.from({ length: 80 }, (_, i) => {
        const spread = ((i * 37) % 100) / 100; // 0..1, deterministic
        const dir = i % 2 ? 1 : -1;
        // Mostly toward the content side (reading direction), a little back over the badge.
        const x = (spread * 560 - 90) * (isRtl ? -1 : 1);
        return (
          <i
            key={i}
            style={{
              left: origin.x,
              top: origin.y,
              background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              ['--x' as string]: `${x}px`,
              ['--up' as string]: `${160 + ((i * 53) % 200)}px`,
              ['--fall' as string]: `${260 + ((i * 29) % 260)}px`,
              ['--r' as string]: `${dir * (360 + ((i * 41) % 540))}deg`,
              ['--d' as string]: `${1.5 + (i % 5) * 0.14}s`,
              ['--delay' as string]: `${(i % 8) * 0.02}s`,
            } as React.CSSProperties}
          />
        );
      })}
    </div>,
    document.body,
  );
};
